import React from "react";
import MapGL, { Marker, NavigationControl, Popup } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import TextFieldDropdown from "@/pages/shared/TextFieldDropdown.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";
import {
  REGIONAL_HEALTH_COUNTRY_VIEWS,
  REGIONAL_HEALTH_SADC_BOUNDS,
  REGIONAL_HEALTH_SADC_DEFAULT_VIEW,
  RegionalHealthMapLegendRows,
  RegionalHealthMapScopeControls,
  resolveRegionalHealthCountry,
  resolveRegionalHealthCountryLabel,
} from "@/pages/regional-health/RegionalHealthMapPresentation.jsx";
import { regionalHealthService } from "@/services/regionalHealthService.js";
import supabase from "@/lib/supabase";

const VOLUME_BANDS = [
  { key: "5_19", color: "#90B1B3" },
  { key: "20_49", color: "#5C7D7F" },
  { key: "50_plus", color: "#2C4C4E" },
];
const DEFAULT_FILTERS = { facilityId: "", programmeId: "", period: "last_30_days", workflowState: "", dataQualityState: "" };
const FILTER_CHEVRON = "/icons/pharmacy/drop-down-chevron.svg";
const MAP_DIAGNOSTIC_LIMIT = 40;

function recordInstitutionMapDiagnostic(stage, detail = {}) {
  if (typeof window === "undefined") return;
  const current = Array.isArray(window.__AFRIMED_MAP_DIAGNOSTICS__)
    ? window.__AFRIMED_MAP_DIAGNOSTICS__
    : [];
  current.push({
    at: new Date().toISOString(),
    surface: "institution-regional-health-map",
    stage,
    ...detail,
  });
  window.__AFRIMED_MAP_DIAGNOSTICS__ = current.slice(-MAP_DIAGNOSTIC_LIMIT);
}

function sanitiseMapError(error) {
  return String(error?.message || error || "unknown")
    .replace(/https?:\/\/[^\s)]+/gi, "[resource]")
    .replace(/access_token=[^&\s)]+/gi, "access_token=[redacted]")
    .slice(0, 180);
}

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function periodBounds(preset) {
  const end = new Date();
  const start = new Date(end);
  if (preset === "last_90_days") start.setUTCDate(start.getUTCDate() - 89);
  else if (preset === "current_year") start.setUTCMonth(0, 1);
  else start.setUTCDate(start.getUTCDate() - 29);
  return { reportingPeriodStart: isoDate(start), reportingPeriodEnd: isoDate(end) };
}

function validMarkers(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const latitude = Number(row?.approximateLatitude);
    const longitude = Number(row?.approximateLongitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude)
      && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
      && row?.displayPrecision === "approximate";
  });
}

function authorisedRegionalScope(facilities) {
  const authorisedFacilities = Array.isArray(facilities) ? facilities : [];
  const reportingCountries = new Set(
    authorisedFacilities
      .filter((facility) => facility?.readinessStatus === "ready" && facility?.aggregateReportingEnabled === true)
      .map((facility) => resolveRegionalHealthCountry(facility?.country))
      .filter(Boolean),
  );
  if (reportingCountries.size === 1) return { country: [...reportingCountries][0], sadc: true };
  if (reportingCountries.size > 1) return { country: "", sadc: false };

  const institutionCountries = new Set(
    authorisedFacilities
      .map((facility) => resolveRegionalHealthCountry(facility?.country))
      .filter(Boolean),
  );
  if (institutionCountries.size !== 1) return { country: "", sadc: false };
  return { country: [...institutionCountries][0], sadc: true };
}

function workflowLabel(label, value) {
  const catalogueKey = value === "needs_review" ? "needsReview" : value;
  return label(`status.${catalogueKey}`, value);
}

function HeatmapMarkerDetails({ marker, label, displayDate, onOpenSignal, onOpenAudit }) {
  const period = marker?.reportingPeriod || {};
  const periodLabel = period.start === period.end
    ? displayDate(period.start)
    : `${displayDate(period.start)} – ${displayDate(period.end)}`;
  return <div className="institution-heatmap-detail">
    <strong>{marker.facilityName}</strong>
    <dl>
      <dt>{label("heatmap.fields.area", "Approved approximate area")}</dt><dd>{marker.approximateArea}</dd>
      <dt>{label("heatmap.fields.programme", "Programme")}</dt><dd>{marker.programme}</dd>
      <dt>{label("heatmap.fields.period", "Reporting period")}</dt><dd>{periodLabel}</dd>
      <dt>{label("heatmap.fields.volume", "Reporting volume")}</dt><dd className="institution-heatmap-detail__volume"><span className={`institution-heatmap-volume-swatch institution-heatmap-volume-swatch--${marker.reportingVolumeBand}`} aria-hidden="true" />{label(`heatmap.volume.${marker.reportingVolumeBand}`, marker.reportingVolumeBand)}</dd>
      <dt>{label("heatmap.fields.workflow", "Workflow state")}</dt><dd>{workflowLabel(label, marker.workflowState)}</dd>
      <dt>{label("heatmap.fields.quality", "Data-quality state")}</dt><dd>{label(`heatmap.quality.${marker.dataQualityState}`, marker.dataQualityState)}</dd>
    </dl>
    <div className="institution-heatmap-detail__actions">
      {marker.safeSignalReference ? <Button type="button" variant="outline" size="sm" onClick={() => onOpenSignal(marker.safeSignalReference)}>{label("heatmap.actions.openSignal", "Open signal")}</Button> : null}
      <Button type="button" variant="text" size="sm" onClick={onOpenAudit}>{label("heatmap.actions.openAudit", "Open audit trail")}</Button>
    </div>
  </div>;
}

export default function InstitutionRegionalHealthHeatmap({
  institutionId,
  facilities = [],
  programmes = [],
  onOpenSignal,
  onOpenAudit,
}) {
  const { t, formatDate } = useLanguage();
  const { isDark } = useTheme();
  const label = React.useCallback((key, fallback) => t(`regionalHealthSignals.${key}`, fallback), [t]);
  const mapRef = React.useRef(null);
  const mapFrameRef = React.useRef(null);
  const mapReadyRef = React.useRef(false);
  const mapStartedAtRef = React.useRef(0);
  const token = String(import.meta.env.VITE_MAPBOX_TOKEN || "").trim();
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [snapshot, setSnapshot] = React.useState({ markers: [], notice: "no_authorised_geography" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [selectedMarkerId, setSelectedMarkerId] = React.useState("");
  const [syntheticMarkers, setSyntheticMarkers] = React.useState([]);
  const [selectedSyntheticReference, setSelectedSyntheticReference] = React.useState("");
  const [mapRuntimeState, setMapRuntimeState] = React.useState("idle");
  const [mapInstanceKey, setMapInstanceKey] = React.useState(0);
  const scope = React.useMemo(() => authorisedRegionalScope(facilities), [facilities]);
  const countryView = REGIONAL_HEALTH_COUNTRY_VIEWS[scope.country] || null;
  const countryLabel = resolveRegionalHealthCountryLabel(t, scope.country);
  const [focusMode, setFocusMode] = React.useState("country");

  const markers = React.useMemo(() => validMarkers(snapshot.markers), [snapshot.markers]);
  const selectedMarker = markers.find((marker) => marker.markerId === selectedMarkerId) || null;
  const selectedSynthetic = syntheticMarkers.find((marker) => marker.journeyReference === selectedSyntheticReference) || null;
  const displayDate = React.useCallback((value) => value ? formatDate(value, { dateStyle: "medium" }) : "—", [formatDate]);

  const load = React.useCallback(async (nextFilters) => {
    if (!institutionId) return;
    setLoading(true);
    setError("");
    const [result, synthetic] = await Promise.all([regionalHealthService.loadInstitutionHeatmapSnapshot({
      institutionId,
      facilityId: nextFilters.facilityId,
      programmeId: nextFilters.programmeId,
      ...periodBounds(nextFilters.period),
      workflowState: nextFilters.workflowState,
      dataQualityState: nextFilters.dataQualityState,
    }), supabase.rpc("institution_synthetic_journey_map_snapshot", { target_institution: institutionId })]);
    const bounds = periodBounds(nextFilters.period);
    setSyntheticMarkers((Array.isArray(synthetic.data) ? synthetic.data : []).filter((marker) => {
      const day = String(marker.createdAt || "").slice(0, 10);
      return day >= bounds.reportingPeriodStart && day <= bounds.reportingPeriodEnd
        && (!nextFilters.facilityId || marker.sourceFacilityId === nextFilters.facilityId)
        && (!nextFilters.programmeId || marker.programId === nextFilters.programmeId)
        && Number.isFinite(Number(marker.approximateLatitude))
        && Number.isFinite(Number(marker.approximateLongitude));
    }));
    setLoading(false);
    if (result.error) {
      setError(String(result.error?.message || result.error));
      setSnapshot({ markers: [], notice: "unavailable" });
      return;
    }
    setSelectedMarkerId("");
    setSelectedSyntheticReference("");
    setSnapshot({ markers: result.data?.markers || [], notice: result.data?.notice || "no_aggregate_data" });
  }, [institutionId]);

  React.useEffect(() => { load(DEFAULT_FILTERS); }, [institutionId, load]);

  const fitScope = React.useCallback((nextFocus = focusMode) => {
    const map = mapRef.current?.getMap?.() || mapRef.current;
    if (!map) return;
    if (nextFocus === "country" && countryView?.bounds) {
      map.fitBounds?.(countryView.bounds, {
        padding: { top: 24, right: 24, bottom: 24, left: 24 },
        duration: 900,
      });
      return;
    }
    if (scope.sadc) {
      map.fitBounds?.(REGIONAL_HEALTH_SADC_BOUNDS, {
        padding: { top: 18, right: 18, bottom: 18, left: 18 },
        duration: 900,
      });
    }
  }, [countryView, focusMode, scope.sadc]);

  React.useEffect(() => {
    if (focusMode === "country" && !countryView) setFocusMode(scope.sadc ? "sadc" : "country");
  }, [countryView, focusMode, scope.sadc]);

  React.useEffect(() => {
    if (!mapFrameRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => mapRef.current?.resize?.());
    observer.observe(mapFrameRef.current);
    return () => observer.disconnect();
  }, []);

  const facilityOptions = [{ value: "", label: label("heatmap.filters.allFacilities", "All authorised facilities") }, ...facilities.map((facility) => ({ value: facility.id, label: facility.name }))];
  const programmeOptions = [{ value: "", label: label("heatmap.filters.allProgrammes", "All authorised programmes") }, ...programmes.map((programme) => ({ value: programme.id, label: programme.name }))];
  const periodOptions = ["last_30_days", "last_90_days", "current_year"].map((value) => ({ value, label: label(`heatmap.period.${value}`, value) }));
  const workflowOptions = [{ value: "", label: label("heatmap.filters.allWorkflow", "All workflow states") }, ...["draft", "submitted", "needs_review", "reconciled", "closed"].map((value) => ({ value, label: workflowLabel(label, value) }))];
  const qualityOptions = [{ value: "", label: label("heatmap.filters.allQuality", "All data-quality states") }, ...["clear", "needs_review"].map((value) => ({ value, label: label(`heatmap.quality.${value}`, value) }))];
  const volumeLegend = VOLUME_BANDS.map((item) => ({
    ...item,
    label: label(`heatmap.volume.${item.key}`, item.key),
  }));
  const setFilter = (key) => (value) => setFilters((current) => ({ ...current, [key]: value }));
  const closeLabel = t("common.close", "Close");
  const successfulSnapshot = !loading && !error;
  const canRenderMap = successfulSnapshot && Boolean(token);
  const shouldRenderMap = canRenderMap && mapRuntimeState !== "error";
  const initialViewState = countryView
    ? { latitude: countryView.center[1], longitude: countryView.center[0], zoom: countryView.zoom }
    : REGIONAL_HEALTH_SADC_DEFAULT_VIEW;

  React.useEffect(() => {
    mapReadyRef.current = false;
    mapStartedAtRef.current = performance.now();
    setMapRuntimeState(canRenderMap ? "loading" : token ? "idle" : "missing-config");
    recordInstitutionMapDiagnostic("initialisation", {
      elapsedMs: 0,
      tokenPresent: Boolean(token),
      markerCount: markers.length,
      snapshotReady: successfulSnapshot,
    });
  }, [canRenderMap, institutionId, markers.length, successfulSnapshot, token]);

  const retryMapInitialisation = React.useCallback(() => {
    if (!canRenderMap) return;
    mapReadyRef.current = false;
    mapStartedAtRef.current = performance.now();
    setMapRuntimeState("loading");
    setMapInstanceKey((value) => value + 1);
    recordInstitutionMapDiagnostic("manual-retry", { elapsedMs: 0, markerCount: markers.length });
  }, [canRenderMap, markers.length]);

  return <section className="institution-heatmap" aria-labelledby="institution-heatmap-title">
    <header className="institution-heatmap__header">
      <h3 id="institution-heatmap-title" className="text-title-large text-on-surface">{label("heatmap.title", "Institution reporting map")}</h3>
      <p className="text-body-large text-on-surface-var">{label("heatmap.body", "Approximate, authorised aggregate reporting geography only. Exact locations are never displayed.")}</p>
      <span className="institution-heatmap__precision text-label-large text-on-surface">{label("heatmap.approximateOnly", "Approximate areas only")}</span>
    </header>

    <div className="institution-heatmap-filters" aria-label={label("heatmap.filters.label", "Map filters")}>
      <TextFieldDropdown id="institution-heatmap-facility" name="facilityId" className="institution-heatmap-filter" variant="availability" label={label("heatmap.filters.facility", "Facility")} value={filters.facilityId} placeholder={label("heatmap.filters.allFacilities", "All authorised facilities")} onChange={(event) => setFilter("facilityId")(event.target.value)} options={facilityOptions} triggerClassName="institution-heatmap-filter-trigger" indicatorSrc={FILTER_CHEVRON} mobileSheet mobileSheetTitle={label("heatmap.filters.facility", "Facility")} mobileSheetCloseLabel={closeLabel} />
      <TextFieldDropdown id="institution-heatmap-programme" name="programmeId" className="institution-heatmap-filter" variant="availability" label={label("heatmap.filters.programme", "Programme")} value={filters.programmeId} placeholder={label("heatmap.filters.allProgrammes", "All authorised programmes")} onChange={(event) => setFilter("programmeId")(event.target.value)} options={programmeOptions} triggerClassName="institution-heatmap-filter-trigger" indicatorSrc={FILTER_CHEVRON} mobileSheet mobileSheetTitle={label("heatmap.filters.programme", "Programme")} mobileSheetCloseLabel={closeLabel} />
      <TextFieldDropdown id="institution-heatmap-period" name="period" className="institution-heatmap-filter" variant="availability" label={label("heatmap.filters.period", "Reporting period")} value={filters.period} placeholder={label("heatmap.filters.period", "Reporting period")} onChange={(event) => setFilter("period")(event.target.value)} options={periodOptions} triggerClassName="institution-heatmap-filter-trigger" indicatorSrc={FILTER_CHEVRON} mobileSheet mobileSheetTitle={label("heatmap.filters.period", "Reporting period")} mobileSheetCloseLabel={closeLabel} />
      <TextFieldDropdown id="institution-heatmap-workflow" name="workflowState" className="institution-heatmap-filter" variant="availability" label={label("heatmap.filters.workflow", "Workflow state")} value={filters.workflowState} placeholder={label("heatmap.filters.allWorkflow", "All workflow states")} onChange={(event) => setFilter("workflowState")(event.target.value)} options={workflowOptions} triggerClassName="institution-heatmap-filter-trigger" indicatorSrc={FILTER_CHEVRON} mobileSheet mobileSheetTitle={label("heatmap.filters.workflow", "Workflow state")} mobileSheetCloseLabel={closeLabel} />
      <TextFieldDropdown id="institution-heatmap-quality" name="dataQualityState" className="institution-heatmap-filter" variant="availability" label={label("heatmap.filters.quality", "Data-quality state")} value={filters.dataQualityState} placeholder={label("heatmap.filters.allQuality", "All data-quality states")} onChange={(event) => setFilter("dataQualityState")(event.target.value)} options={qualityOptions} triggerClassName="institution-heatmap-filter-trigger" indicatorSrc={FILTER_CHEVRON} mobileSheet mobileSheetTitle={label("heatmap.filters.quality", "Data-quality state")} mobileSheetCloseLabel={closeLabel} />
      <Button type="button" variant="tonal" loading={loading} onClick={() => load(filters)}>{label("heatmap.actions.apply", "Apply filters")}</Button>
    </div>

    <div ref={mapFrameRef}>
      <Card
        className="institution-heatmap-map"
        data-testid="institution-regional-health-map"
        data-map-state={mapRuntimeState}
      >
      {loading ? <div className="institution-heatmap-empty" role="status"><span className="text-body-large text-on-surface-var">{label("heatmap.loading", "Loading map…")}</span></div> : null}
      {!loading && error ? <div className="institution-heatmap-empty" role="alert"><strong className="text-title-medium text-on-surface">{label("heatmap.empty.unavailableTitle", "Map unavailable")}</strong><p className="text-body-large text-on-surface-var">{label("heatmap.empty.unavailable", "The authorised aggregate snapshot could not be loaded. Try again later.")}</p><Button type="button" variant="tonal" onClick={() => load(filters)}>{label("retry", "Try again")}</Button></div> : null}
      {shouldRenderMap ? <MapGL
          key={mapInstanceKey}
          ref={mapRef}
          mapboxAccessToken={token}
          initialViewState={initialViewState}
          mapStyle={getAfriMedMapStyle(isDark)}
          dragRotate={false}
          touchPitch={false}
          attributionControl={false}
          onLoad={() => {
            mapReadyRef.current = true;
            setMapRuntimeState("ready");
            mapRef.current?.resize?.();
            fitScope();
            recordInstitutionMapDiagnostic("map-load", {
              elapsedMs: Math.round(performance.now() - mapStartedAtRef.current),
              markerCount: markers.length,
            });
          }}
          onError={(event) => {
            if (!mapReadyRef.current) {
              setMapRuntimeState("error");
              recordInstitutionMapDiagnostic("mapbox-error", {
                elapsedMs: Math.round(performance.now() - mapStartedAtRef.current),
                message: sanitiseMapError(event?.error || event),
              });
            }
          }}
          style={{ position: "absolute", inset: 0 }}
        >
          {markers.map((marker) => <Marker key={marker.markerId} latitude={Number(marker.approximateLatitude)} longitude={Number(marker.approximateLongitude)} anchor="center"><button type="button" className={`institution-heatmap-marker institution-heatmap-marker--${marker.reportingVolumeBand}`} aria-label={`${marker.facilityName}, ${label(`heatmap.volume.${marker.reportingVolumeBand}`, marker.reportingVolumeBand)}`} onClick={() => setSelectedMarkerId(marker.markerId)} /></Marker>)}
          {syntheticMarkers.map((marker) => <Marker key={marker.journeyReference} latitude={Number(marker.approximateLatitude)} longitude={Number(marker.approximateLongitude)} anchor="center"><button type="button" className="institution-heatmap-marker" style={{ border: "3px dashed #7c3aed", backgroundColor: "#c4b5fd" }} aria-label={`Synthetic QA health-zone arrival aggregate, ${marker.journeyReference}`} onClick={() => setSelectedSyntheticReference(marker.journeyReference)} /></Marker>)}
          <NavigationControl position="bottom-right" showCompass={false} />
          {selectedMarker ? <Popup latitude={Number(selectedMarker.approximateLatitude)} longitude={Number(selectedMarker.approximateLongitude)} closeOnClick={false} onClose={() => setSelectedMarkerId("")} maxWidth="320px"><HeatmapMarkerDetails marker={selectedMarker} label={label} displayDate={displayDate} onOpenSignal={onOpenSignal} onOpenAudit={onOpenAudit} /></Popup> : null}
          {selectedSynthetic ? <Popup latitude={Number(selectedSynthetic.approximateLatitude)} longitude={Number(selectedSynthetic.approximateLongitude)} closeOnClick={false} onClose={() => setSelectedSyntheticReference("")} maxWidth="320px"><div className="text-body-small"><strong>Synthetic QA health zone</strong><p>{selectedSynthetic.journeyReference}</p><p>{selectedSynthetic.arrivedCount} synthetic arrival aggregate · not real surveillance or clinical data</p></div></Popup> : null}
        </MapGL> : null}
      {successfulSnapshot && (!token || mapRuntimeState === "error") ? <div className="institution-heatmap-empty"><strong className="text-title-medium text-on-surface">{label("heatmap.empty.mapUnavailableTitle", "Map unavailable")}</strong><p className="text-body-large text-on-surface-var">{label("heatmap.empty.mapUnavailable", "The accessible result list remains available below.")}</p>{token ? <Button type="button" variant="tonal" onClick={retryMapInitialisation}>{label("retry", "Try again")}</Button> : null}</div> : null}
      </Card>
    </div>

    {successfulSnapshot ? <section className="institution-heatmap-presentation">
      {countryView ? <RegionalHealthMapScopeControls
        activeScope={focusMode}
        countryLabel={countryLabel}
        countryEnabled={Boolean(countryView)}
        sadcEnabled={scope.sadc}
        resetLabel={label("heatmap.actions.reset", "Reset view")}
        onCountry={() => { setFocusMode("country"); fitScope("country"); }}
        onSadc={() => { if (!scope.sadc) return; setFocusMode("sadc"); fitScope("sadc"); }}
        onReset={() => fitScope(focusMode)}
      /> : null}
      {canRenderMap && markers.length === 0 ? <div className="institution-heatmap-empty-inline" role="status"><strong className="text-title-medium text-on-surface">{label("heatmap.empty.no_authorised_geographyTitle", "No eligible reporting geography")}</strong><p className="text-body-large text-on-surface-var">{label("heatmap.empty.no_authorised_geography", "No approved approximate geography is available for the current selection.")}</p></div> : null}
      {syntheticMarkers.length ? <Card className="p-4 space-y-2" data-testid="synthetic-health-zone-arrivals"><h4 className="text-title-medium">Synthetic QA health-zone arrivals</h4><p className="text-body-small text-on-surface-variant">Separate staging demonstration layer. These markers do not count as authorised real reporting signals and do not alter real-map suppression.</p>{syntheticMarkers.map((marker) => <p key={marker.journeyReference} className="text-body-small">{marker.journeyReference} · {marker.arrivedCount} synthetic arrival aggregate · {displayDate(marker.createdAt)}</p>)}</Card> : null}
      <Card className="institution-heatmap-legend-card py-3" aria-label={label("heatmap.legend", "Reporting volume")}>
        <div className="institution-heatmap-legend-copy px-4">
          <h4 className="text-title-medium text-on-surface">{label("heatmap.legend", "Reporting volume")}</h4>
          <p className="text-body-medium text-on-surface-var">{label("heatmap.legendUnit", "Authorised aggregate reporting units per approximate area")}</p>
        </div>
        <RegionalHealthMapLegendRows items={volumeLegend} />
        <p className="institution-heatmap-legend-note px-4 text-body-medium text-on-surface-var">{label("heatmap.legendClarification", "Colour intensity shows reporting volume, not severity or risk.")}</p>
      </Card>
    </section> : null}

    {successfulSnapshot ? <div
      className="institution-heatmap-list"
      aria-label={label("heatmap.listTitle", "Accessible map results")}
      data-testid="institution-regional-health-map-results"
    >
      <h4>{label("heatmap.listTitle", "Accessible map results")}</h4>
      {markers.length === 0 ? <p className="text-body-medium text-on-surface-var" role="status">{label("heatmap.empty.no_authorised_geography", "No approved approximate geography is available for the current selection.")}</p> : null}
      {markers.map((marker) => <article key={marker.markerId}><HeatmapMarkerDetails marker={marker} label={label} displayDate={displayDate} onOpenSignal={onOpenSignal} onOpenAudit={onOpenAudit} /></article>)}
    </div> : null}
  </section>;
}
